// @figma file=pkCGby54zXzTsccXvHqzO6 node=14831:272551 name="Otp Input" set="shadcn-studio-my-taible" verified=2026-03-21

import { OTPInput, OTPInputContext, REGEXP_ONLY_DIGITS } from 'input-otp';
import { Minus } from 'lucide-react';
import * as React from 'react';
import { cn } from '../../../lib/utils';

// ─── Internal context (error-state propagation) ───────────────────────────────

interface InputOTPInternalContext {
  hasError: boolean;
}

const InputOTPInternalContext = React.createContext<InputOTPInternalContext>({
  hasError: false,
});

// ─── InputOTP (root) ──────────────────────────────────────────────────────────

/**
 * Extend OTPInput's own props (which is a discriminated union) via intersection
 * — must use `type`, not `interface`, to avoid TS2312.
 */
export type InputOTPProps = React.ComponentPropsWithoutRef<typeof OTPInput> & {
  /** Show destructive (error) borders on all slots — Figma State=Destructive Default/Active. */
  hasError?: boolean;
};

/**
 * One-time password input with segmented digit slots.
 * Wraps the `input-otp` library's `OTPInput` with AvilaRoffe theme tokens.
 *
 * @example
 * <InputOTP maxLength={6}>
 *   <InputOTPGroup>
 *     <InputOTPSlot index={0} />
 *     <InputOTPSlot index={1} />
 *     <InputOTPSlot index={2} />
 *   </InputOTPGroup>
 *   <InputOTPSeparator />
 *   <InputOTPGroup>
 *     <InputOTPSlot index={3} />
 *     <InputOTPSlot index={4} />
 *     <InputOTPSlot index={5} />
 *   </InputOTPGroup>
 * </InputOTP>
 */
const InputOTP = React.forwardRef<React.ElementRef<typeof OTPInput>, InputOTPProps>(
  (
    { className, containerClassName, hasError = false, pattern = REGEXP_ONLY_DIGITS, ...props },
    ref,
  ) => (
    <InputOTPInternalContext.Provider value={{ hasError }}>
      <OTPInput
        ref={ref}
        pattern={pattern}
        containerClassName={cn(
          'flex items-center gap-2 has-[:disabled]:opacity-50',
          containerClassName,
        )}
        className={cn('disabled:cursor-not-allowed', className)}
        {...props}
      />
    </InputOTPInternalContext.Provider>
  ),
);
InputOTP.displayName = 'InputOTP';

// ─── InputOTPGroup ────────────────────────────────────────────────────────────

export interface InputOTPGroupProps extends React.HTMLAttributes<HTMLDivElement> {}

/**
 * Groups a set of `InputOTPSlot` elements visually — applies the connected
 * border treatment (first slot rounded-start, last slot rounded-end).
 */
const InputOTPGroup = React.forwardRef<HTMLDivElement, InputOTPGroupProps>(
  ({ className, ...props }, ref) => (
    // biome-ignore lint/a11y/useSemanticElements: OTP group uses div to avoid fieldset reset styles affecting the OTP input layout
    <div ref={ref} className={cn('flex items-center', className)} role="group" {...props} />
  ),
);
InputOTPGroup.displayName = 'InputOTPGroup';

// ─── InputOTPSlot ─────────────────────────────────────────────────────────────

export interface InputOTPSlotProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Zero-based index of the slot within the OTPInput. */
  index: number;
}

/**
 * A single digit slot. Reads its state (char, isActive, hasFakeCaret) from
 * `OTPInputContext` provided by the library.
 *
 * Figma token mapping:
 *   - Default            → `border-border` 1px  (stroke_2652P7 #E5E5E5)
 *   - Active             → `ring-2 ring-ring`   (stroke_F7RKKX #A3A3A3 3px)
 *   - Destructive Dflt   → `border-destructive` (stroke_3FKSQK #DC2626)
 *   - Destructive Active → `ring-2 ring-destructive/20` (stroke_6JKJ9C rgba(224,52,52,.2) 3px)
 */
const InputOTPSlot = React.forwardRef<HTMLDivElement, InputOTPSlotProps>(
  ({ index, className, ...props }, ref) => {
    const otpCtx = React.useContext(OTPInputContext);
    const { hasError } = React.useContext(InputOTPInternalContext);
    const slot = otpCtx.slots[index];

    if (!slot) return null;
    const { char, hasFakeCaret, isActive } = slot;

    return (
      <div
        ref={ref}
        data-active={isActive || undefined}
        data-invalid={hasError || undefined}
        className={cn(
          // Base layout — 36×36 px from Figma, Tailwind equivalent: h-9 w-9
          'relative flex h-9 w-9 items-center justify-center',
          // Shared borders within the group (each slot owns top/bottom/right)
          'border-y border-e border-border text-sm shadow-sm transition-all',
          // First slot in group: add start border + start rounding
          'first:rounded-s-[var(--radius)] first:border-s',
          // Last slot in group: add end rounding
          'last:rounded-e-[var(--radius)]',
          // Active/focused — Figma stroke_F7RKKX (#A3A3A3, 3px)
          isActive && !hasError && 'z-10 ring-2 ring-ring ring-offset-background',
          // Error border — Figma stroke_3FKSQK (#DC2626, 1px)
          hasError && 'border-destructive',
          // Error + active ring — Figma stroke_6JKJ9C (rgba(224,52,52,.2), 3px)
          hasError && isActive && 'z-10 ring-2 ring-destructive/20 ring-offset-background',
          className,
        )}
        {...props}
      >
        {char}
        {hasFakeCaret && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div className="animate-caret-blink h-4 w-px bg-foreground duration-1000" />
          </div>
        )}
      </div>
    );
  },
);
InputOTPSlot.displayName = 'InputOTPSlot';

// ─── InputOTPSeparator ────────────────────────────────────────────────────────

export interface InputOTPSeparatorProps extends React.HTMLAttributes<HTMLDivElement> {}

/**
 * Visual separator between slot groups (renders a dash icon).
 * Figma: "Seprator" IMAGE-SVG node between slot groups.
 *
 * role="separator" + aria-hidden="true": decorative structural separator — hidden from
 * the a11y tree intentionally. Non-focusable separators do not require aria-valuenow
 * per WAI-ARIA spec (only required when the separator is a focusable splitter widget).
 */
const InputOTPSeparator = React.forwardRef<HTMLDivElement, InputOTPSeparatorProps>(
  ({ className, ...props }, ref) => (
    // biome-ignore lint/a11y/useFocusableInteractive: aria-hidden="true" removes this from a11y tree — keyboard navigation not required
    // biome-ignore lint/a11y/useSemanticElements: <hr> is void; Minus icon visual requires a container element
    <div
      ref={ref}
      role="separator"
      aria-valuenow={0}
      aria-hidden="true"
      className={cn('flex items-center text-muted-foreground', className)}
      {...props}
    >
      <Minus className="h-4 w-4" />
    </div>
  ),
);
InputOTPSeparator.displayName = 'InputOTPSeparator';

// ─── Exports ──────────────────────────────────────────────────────────────────

export type { OTPInputProps } from 'input-otp';
export { InputOTP, InputOTPGroup, InputOTPSeparator, InputOTPSlot, REGEXP_ONLY_DIGITS };
